import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { ValidezNacionalI } from "interfaces/ValidezNacionalI";
import { NormativaI } from "interfaces/Normativa";
import {
  getEstadoValidezNacional,
  postValidezNacional,
  putValidezNacional,
} from "services/validezNacional";
import { createValidezNacionalSchema } from "schemas/videzNacional/createValidezNacionalSchema";
import { formatDate } from "utils/formatUtils";
import { EstadoValidacionNacionalI } from "interfaces/EstadoValidacionNacional";
import { PlanEstudioI } from "interfaces/PlanEstudio";
import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import { getNormativas } from "services/normativas";
import { getPlanesEstudio } from "services/planesEstudio";
import { Default } from "react-toastify/dist/utils";
import { DefaultSelectedOptionI } from "interfaces/DefaultSelectedOption";
import { log } from "console";

interface Props {
  actionType: "create" | "edit" | undefined;
  onClose?: any;
  elemento?: ValidezNacionalI;
  onSuccess?: (titulo: ValidezNacionalI) => void;
  showModal: boolean;
}

interface initialValues {
  id?: string | number;
  fecha_aprobacion: string;
  fecha_caducidad: string;
  plan_estudio_id: string | number;
  estado_id: string | number;
  normativa_id: string | number;
  expediente_interno: string | number;
  expediente_nacional: string | number;
}

interface defaultOptionsSelected {
  default_selected_estado: DefaultSelectedOptionI | null;
  default_selected_normativa: DefaultSelectedOptionI | null;
  default_selected_plan_estudio: DefaultSelectedOptionI | null;
}

export const ValidacionNacionalABM = ({
  showModal,
  actionType,
  onClose,
  elemento,
  onSuccess,
}: Props) => {
  const isEditMode = actionType == "edit";
  const initialValues: initialValues = {
    fecha_aprobacion: "",
    fecha_caducidad: "",
    plan_estudio_id: "",
    estado_id: "",
    normativa_id: "",
    expediente_interno: "",
    expediente_nacional: "",
  };

  const [isLoading, setIsLoading] = useState(false);

  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelected>({
      default_selected_estado: null,
      default_selected_normativa: null,
      default_selected_plan_estudio: null,
    });

  const renderInitialValues = async () => {
    let currentValues;
    if (isEditMode && elemento) {
      currentValues = {
        id: elemento?.id ?? "",
        fecha_aprobacion: elemento?.fechaAprobacion
          ? formatDate.formDate(elemento.fechaAprobacion)
          : "",
        fecha_caducidad: elemento?.fechaCaducidad
          ? formatDate.formDate(elemento.fechaCaducidad)
          : "",
        plan_estudio_id: elemento?.planEstudio?.id ?? "",
        estado_id: elemento?.estado?.id ?? "",
        normativa_id: elemento?.normativa?.id ?? "",
        expediente_interno: elemento?.expedienteInterno ?? "",
        expediente_nacional: elemento?.expedienteNacional ?? "",
      };
      setDefaultOptionsSelected({
        default_selected_estado: elemento?.estado
          ? {
              id: elemento.estado.id,
              nombre: elemento.estado.nombre,
            }
          : null,
        default_selected_normativa: elemento?.normativa
          ? {
              id: elemento.normativa.id,
              nombre: elemento.normativa.nombre,
            }
          : null,
        default_selected_plan_estudio: elemento?.planEstudio
          ? {
              id: elemento.planEstudio.id,
              nombre: elemento.planEstudio.nombre,
            }
          : null,
      });
    } else {
      currentValues = initialValues;
    }
    setValues(currentValues);
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      /* if (params.fecha_publicacion) {
        params.fecha_publicacion = new Date(
          `${params.fecha_publicacion}T00:00:00`,
        ).toISOString();
      } */
      setIsLoading(true);
      if (params.fecha_aprobacion) {
        params.fecha_aprobacion = new Date(
          `${params.fecha_aprobacion}T00:00:00`,
        ).toISOString();
      }
      if (params.fecha_caducidad) {
        params.fecha_caducidad = new Date(
          `${params.fecha_caducidad}T00:00:00`,
        ).toISOString();
      }
      const newValidacionNacional = await postValidezNacional({ params });
      showSuccessToast("¡La validación nacional fue creado con éxito!");
      if (onSuccess) onSuccess(newValidacionNacional);
      onClose();
    } catch (err: any) {
      showErrorToast("La validación nacional no pudo ser creada.");
      console.log(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      /* if (params.fecha_publicacion) {
        params.fecha_publicacion = new Date(
          `${params.fecha_publicacion}T00:00:00`,
        ).toISOString();
      } */
      setIsLoading(true);
      if (params.fecha_aprobacion) {
        params.fecha_aprobacion = new Date(
          `${params.fecha_aprobacion}T00:00:00`,
        ).toISOString();
      }
      if (params.fecha_caducidad) {
        params.fecha_caducidad = new Date(
          `${params.fecha_caducidad}T00:00:00`,
        ).toISOString();
      }

      const newValidacionNacional = await putValidezNacional({ params });
      showSuccessToast("¡La validación nacional fue editada con éxito!");
      if (onSuccess) onSuccess(newValidacionNacional);
      onClose();
    } catch (err: any) {
      showErrorToast("La validación nacional no pudo ser editada.");
      console.log(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setValues(initialValues);
    setDefaultOptionsSelected({
      default_selected_estado: null,
      default_selected_normativa: null,
      default_selected_plan_estudio: null,
    });
    if (onClose) onClose();
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: createValidezNacionalSchema,
    onSubmit: actionType == "create" ? handleSubmitCreate : handleSubmitEdit,
  });
  console.log("Errors", errors);

  const { loadingAppData, tiposNormativa } = useSelector(
    (st: RootState) => st.appData,
  );

  useEffect(() => {
    resetForm();
    if (actionType) {
      renderInitialValues();
    }
    if (actionType === "create") {
      setValues(initialValues);
      setDefaultOptionsSelected({
        default_selected_estado: null,
        default_selected_normativa: null,
        default_selected_plan_estudio: null,
      });
    }
  }, [actionType]);

  return (
    <>
      <Modal
        show={showModal}
        onHide={handleClose}
        onClose={handleClose}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>
            {isEditMode
              ? "Editar Validez Nacional"
              : "Nueva Validez Nacional"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Col>
              <Row>
                <Col sm={6}>
                  <Input
                    id={"fecha_aprobacion"}
                    title="Fecha de aprobación"
                    required
                    type={"date"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6}>
                  <Input
                    id={"fecha_caducidad"}
                    title="Fecha de caducidad"
                    type={"date"}
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col sm={6}>
                  <Input
                    id={"expediente_interno"}
                    title="N° Expediente Interno"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6}>
                  <Input
                    id={"expediente_nacional"}
                    title="N° Expediente Nacional"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col sm={6} className="mb-3">
                  <Row className="">
                    <Col sm={12} className="mb-3">
                      <AsyncSelect
                        onChange={handleChange}
                        id="plan_estudio_id"
                        values={values}
                        errors={errors}
                        touched={touched}
                        onBlur={handleBlur}
                        placeholder={"Buscar nombre del plan de estudio"}
                        size="lg"
                        title="Plan de estudio"
                        getter={getPlanesEstudio}
                        paramKey={"nombre"}
                        variant="gray-container"
                        arrElementsPath="elementos"
                        optionsFormatConfig={{
                          value: "id",
                          label: (el: PlanEstudioI) => `${el.nombre}`,
                        }}
                        defaultSelectedOptions={
                          defaultOptionsSelected.default_selected_plan_estudio
                        }
                        noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                        emptySelectMessage={`Ingresar un nombre`}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col sm={6} className="mb-3">
                  <Row className="">
                    <Col sm={12}>
                      <AsyncSelect
                        onChange={handleChange}
                        id="estado_id"
                        values={values}
                        errors={errors}
                        touched={touched}
                        onBlur={handleBlur}
                        placeholder={"Buscar nombre del estado"}
                        size="lg"
                        title="Estado"
                        getter={getEstadoValidezNacional}
                        paramKey={"nombre"}
                        variant="gray-container"
                        arrElementsPath="elementos"
                        optionsFormatConfig={{
                          value: "id",
                          label: (el: EstadoValidacionNacionalI) =>
                            `${el.nombre}`,
                        }}
                        defaultSelectedOptions={
                          defaultOptionsSelected.default_selected_estado
                        }
                        noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                        emptySelectMessage={`Ingresar un nombre`}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col sm={6} className="mb-3">
                  <Row className="">
                    <Col sm={12}>
                      <AsyncSelect
                        onChange={handleChange}
                        id="normativa_id"
                        values={values}
                        errors={errors}
                        touched={touched}
                        onBlur={handleBlur}
                        placeholder={"Buscar nombre de la normativa"}
                        size="lg"
                        title="Normativa"
                        getter={getNormativas}
                        paramKey={"nombre"}
                        variant="gray-container"
                        arrElementsPath="elementos"
                        optionsFormatConfig={{
                          value: "id",
                          label: (el: NormativaI) => `${el.nombre}`,
                        }}
                        defaultSelectedOptions={
                          defaultOptionsSelected.default_selected_normativa
                        }
                        noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                        emptySelectMessage={`Ingresar un nombre`}
                      />
                    </Col>
                    {/* <Col sm={1}>
                      <Button
                        // onClick={() => handleCreateModal("organismos")}
                        variant="primary"
                        size={"lg"}
                        style={{
                          width: "50px",
                          height: "50px",
                          marginTop: "20px",
                          borderRadius: "50%",
                          fontSize: "1.6rem",
                        }}
                      >
                        {" "}
                        +
                      </Button>
                    </Col> */}
                  </Row>
                </Col>
              </Row>
              <Row></Row>
            </Col>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="mx-3"
              variant="secondary"
              size="lg"
              onClick={handleClose}
            >
              Cancelar
            </Button>
            {!isLoading && (
              <Button
                variant="primary"
                size="lg"
                className="user-submit-button"
                /* disabled={isSubmitting} */ type="submit"
              >
                {isEditMode ? "Guardar Cambios" : "Confirmar"}
              </Button>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center w-full">
                <Spinner />
              </div>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
